import { BG_IMAGE_CATALOG } from "../lib/bgAssets";
import {
    IPHONE_BG_PLAY_FALLBACK,
    SCREENSHOT_IPHONE_BG_CATALOG,
} from "../lib/screenshotIphoneBackgrounds";

export const wordWheelScreenshotAccent = "#6366f1";

export const promoNavy = "#1e293b";
export const promoSubtitle = "#64748b";

/**
 * Capture-frame type. Fixed rem on the design canvas (see ScreenshotDesignFrame);
 * do not use vw — store windows scale the whole canvas.
 */
export const WW_SHOT_HEADLINE_SIZE = "2.55rem";
export const WW_SHOT_SUBTITLE_SIZE = "1.35rem";
export const WW_SHOT_HERO_HEADLINE_SIZE = WW_SHOT_HEADLINE_SIZE;
export const WW_SHOT_CTA_HEADLINE_SIZE = WW_SHOT_HEADLINE_SIZE;
export const WW_SHOT_CTA_SUBTITLE_SIZE = WW_SHOT_SUBTITLE_SIZE;
export const WW_SHOT_CTA_FEATURE_SIZE = "1.15rem";
/** Space under the overlay title so the grid starts below the subtitle. */
export const WW_SHOT_PLAY_TOP_INSET = "168px";

/**
 * Shell gradients for Word Wheel screenshot layouts (must match ScreenshotMarketingLayouts).
 * Used by Google Ads to extend the same background into letterbox sides.
 */
export const WORD_WHEEL_SHELL_BG = {
    hero: "linear-gradient(165deg, #020617 0%, #0c4a6e 40%, #082f49 72%, #020617 100%)",
    gameplay: "linear-gradient(180deg, #0c4a6e 0%, #0369a1 32%, #0e7490 68%, #164e63 100%)",
    progression: "linear-gradient(180deg, #292524 0%, #57534e 40%, #a8a29e 100%)",
    journey: "linear-gradient(180deg, #1c1917 0%, #292524 45%, #44403c 100%)",
    dictionary: "linear-gradient(180deg, #f8fafc 0%, #e0f2fe 45%, #bae6fd 100%)",
    themes: "linear-gradient(135deg, #0c4a6e 0%, #14532d 40%, #1e1b4b 75%, #0f172a 100%)",
    daily: "linear-gradient(180deg, #020617 0%, #0f172a 50%, #1e3a5f 100%)",
    cta: "linear-gradient(165deg, #020617 0%, #0c4a6e 55%, #082f49 100%)",
};

/** Fallback solid under play-scene photos (matches PlayBackground). */
const PLAY_BG_FALLBACK = {
    deep_sea: "#031525",
    beach: "#0c4a6e",
    mountain: "#1e293b",
    road: "#292524",
    classroom: "#1c1917",
    flowers: "#365314",
    island: "#0e7490",
    tropical_island: "#155e75",
    urban: "#1e293b",
    village: "#44403c",
};

/** CSS background that extends a play-scene photo full-bleed (Google Ads letterbox). */
export function wordWheelPlaySceneCssBackground(backgroundKey) {
    if (typeof backgroundKey === "string" && /^https?:\/\//i.test(backgroundKey)) {
        return `center / cover no-repeat url("${backgroundKey}") ${IPHONE_BG_PLAY_FALLBACK}`;
    }
    if (backgroundKey && SCREENSHOT_IPHONE_BG_CATALOG[backgroundKey]) {
        return WORD_WHEEL_SHELL_BG.gameplay;
    }
    const key = backgroundKey && BG_IMAGE_CATALOG[backgroundKey] ? backgroundKey : "deep_sea";
    const url = BG_IMAGE_CATALOG[key];
    const fallback = PLAY_BG_FALLBACK[key] || "#031525";
    return `center / cover no-repeat url("${url}") ${fallback}`;
}

/** Resolve letterbox / shell background for a screenshot scene (Google Ads download + preview). */
export function wordWheelShellBgForScene(scene) {
    const key = scene?.background;
    const layout = scene?.layout || "gameplay";
    if (typeof key === "string" && /^https?:\/\//i.test(key)) {
        return wordWheelPlaySceneCssBackground(key);
    }
    if (key && SCREENSHOT_IPHONE_BG_CATALOG[key]) {
        return WORD_WHEEL_SHELL_BG[layout] || WORD_WHEEL_SHELL_BG.gameplay;
    }
    if (layout === "gameplay" || layout === "clue" || layout === "progression") {
        const playKey = key || (layout === "progression" ? "road" : "deep_sea");
        return wordWheelPlaySceneCssBackground(playKey);
    }
    return WORD_WHEEL_SHELL_BG[layout] || WORD_WHEEL_SHELL_BG.gameplay;
}

/** Soft white shell + cool mist washes for App Store screenshots. */
export const phoneShellSx = {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 42%, #f1f5f9 100%)",
    color: promoNavy,
    pt: "max(env(safe-area-inset-top, 0px), 44px)",
    pb: "env(safe-area-inset-bottom, 0px)",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 0,
        background: [
            "radial-gradient(ellipse 90% 55% at 12% 8%, rgba(148, 163, 184, 0.22) 0%, transparent 58%)",
            "radial-gradient(ellipse 75% 50% at 92% 18%, rgba(203, 213, 225, 0.28) 0%, transparent 55%)",
            "radial-gradient(ellipse 80% 45% at 50% -5%, rgba(255, 255, 255, 0.95) 0%, transparent 50%)",
            "radial-gradient(ellipse 100% 60% at 50% 100%, rgba(226, 232, 240, 0.55) 0%, transparent 58%)",
            "radial-gradient(ellipse 55% 40% at 8% 72%, rgba(165, 243, 252, 0.16) 0%, transparent 55%)",
            "radial-gradient(ellipse 50% 35% at 88% 78%, rgba(153, 246, 228, 0.14) 0%, transparent 52%)",
        ].join(", "),
    },
    "& > *": {
        position: "relative",
        zIndex: 1,
    },
};

export const promoHeaderSx = {
    flexShrink: 0,
    px: 2,
    pt: 1,
    pb: 0.75,
    textAlign: "center",
};

export const promoHeadlineStackSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.75,
    maxWidth: 340,
    mx: "auto",
};

export const promoHeadlineSx = {
    fontWeight: 800,
    fontSize: { xs: "1.75rem", sm: "2rem" },
    lineHeight: 1.15,
    letterSpacing: "0.02em",
    color: promoNavy,
    m: 0,
};

export const promoHeadlineSubtitleSx = {
    fontWeight: 500,
    fontSize: "1rem",
    lineHeight: 1.45,
    color: promoSubtitle,
    m: 0,
};

export const featurePillsRowSx = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 0.75,
    mt: 0.75,
};

export const featurePillSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    px: 1.25,
    py: 0.5,
    borderRadius: 999,
    bgcolor: "rgba(255, 255, 255, 0.88)",
    border: "1px solid rgba(148, 163, 184, 0.35)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: promoNavy,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    backdropFilter: "blur(8px)",
};

export const playRegionSx = {
    position: "relative",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    mx: 1,
    mb: 1.5,
    borderRadius: 3,
    overflow: "hidden",
    bgcolor: "transparent",
    border: "1px solid rgba(148, 163, 184, 0.28)",
    boxShadow: "0 10px 32px rgba(15, 61, 54, 0.16)",
};

export const playRegionInnerSx = {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    px: 0.75,
    pt: 0.5,
    pb: 0.75,
};
